<script setup lang="ts">
import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import {
  ItinerarySegmentPlaceholderType,
  ItinerarySegmentPlaceholder,
} from '@apps/main/new-desktop/modules/itineraryButton';

import { TransportType } from '@libs/trip-planner-interfaces';

import ItineraryTransportSelector from '../ItineraryTransportSelector.vue';
import type { AvailableItineraryFerrySegment } from '../../../models';
import ItineraryTimeline from '../ItineraryTimeline.vue';
import ChangeProductLink from '../ChangeProductLink.vue';

import FerrySegmentHeader from './FerrySegmentHeader.vue';
import FerryProductCard from './FerryProductCard.vue';

interface AvailableFerrySegmentProps {
  segment: AvailableItineraryFerrySegment;
  printMode: boolean;
  shouldHideProductLink: boolean;
}

const props = defineProps<AvailableFerrySegmentProps>();

interface AvailableFerrySegmentEmits {
  (eventName: 'change-upsell'): void;
  (eventName: 'remove-filter', tagId: string): void;
  (eventName: 'select-transport', transportType: TransportType): void;
  (eventName: 'show-details'): void;
  (eventName: 'show-more-filters'): void;
  (eventName: 'suggestion-type-selected', suggestionType: SuggestionType): void;
  (eventName: 'change-ferry'): void;
  (eventName: 'remove-ferry'): void;
}

const emit = defineEmits<AvailableFerrySegmentEmits>();
</script>

<template>
  <ItineraryTimeline
    :segment-type="props.segment.segmentType"
    class="available-ferry-segment"
  >
    <template #dropdown>
      <ItineraryTransportSelector
        v-if="!props.shouldHideProductLink"
        :transport-type="TransportType.Ferry"
        :disabled="props.printMode"
        @select-transport="emit('select-transport', $event)"
      />
      <ItinerarySegmentPlaceholder
        v-else
        :transport-type="ItinerarySegmentPlaceholderType.Ferry"
      />
    </template>
    <template #header>
      <FerrySegmentHeader
        :segment="props.segment"
        :print-mode="props.printMode"
        :should-hide-product-link="props.shouldHideProductLink"
        @show-more-filters="emit('show-more-filters')"
        @remove-filter="emit('remove-filter', $event)"
        @suggestion-type-selected="emit('suggestion-type-selected', $event)"
      />
    </template>

    <template #content>
      <div class="available-ferry-segment-content-wrapper">
        <FerryProductCard
          :product-card-data="props.segment.productCardData"
          :print-mode="props.printMode"
          :should-hide-product-link="props.shouldHideProductLink"
          @change-upsell="emit('change-upsell')"
          @show-details="emit('show-details')"
          @change-ferry="emit('change-ferry')"
          @remove-ferry="emit('remove-ferry')"
        />
        <ChangeProductLink
          v-if="!props.shouldHideProductLink && !props.printMode"
          :link-context="{ segmentType: props.segment.segmentType }"
          class="change-product-link"
        />
      </div>
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.available-ferry-segment {
  .available-ferry-segment-content-wrapper {
    display: flex;
    flex-flow: column;
    gap: var(--spacing-16);

    .change-product-link {
      align-self: flex-end;
    }
  }
}
</style>
